import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    Article as ArticleIcon
} from '@mui/icons-material';
import { useRequiredMemberContext } from '../../hooks/useMemberContext';
import { documentReviewAPI } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

interface DocumentAcceptance {
    id: string;
    userId: string;
    memberProfileId: string;
    documentTemplateId: string;
    acceptedAt: string;
    documentTemplate: {
        id: string;
        title: string;
        description?: string;
        type: 'text' | 'pdf' | 'html' | 'markdown';
        displayOrder: number;
        isRequired: boolean;
    };
}

const DocumentReviewPage: React.FC = () => {
    const { memberProfile, isReady } = useRequiredMemberContext();
    const [acceptedDocuments, setAcceptedDocuments] = useState<DocumentAcceptance[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDocument, setSelectedDocument] = useState<DocumentAcceptance | null>(null);
    const [documentContent, setDocumentContent] = useState<string>('');
    const [loadingContent, setLoadingContent] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);

    useEffect(() => {
        if (isReady && memberProfile) {
            fetchAcceptedDocuments();
        }
    }, [isReady, memberProfile]);

    const fetchAcceptedDocuments = async () => {
        if (!memberProfile?.id) return;

        try {
            setLoading(true);
            setError(null);
            const response = await documentReviewAPI.getAcceptances(memberProfile.id);
            setAcceptedDocuments(response.data);
        } catch (err: any) {
            console.error('Error fetching accepted documents:', err);
            setError(err.response?.data?.message || 'Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentClick = async (document: DocumentAcceptance) => {
        // If PDF, download it directly
        if (document.documentTemplate.type === 'pdf') {
            await handleDownloadDocument(document.documentTemplateId, document.documentTemplate.title);
            return;
        }

        // Otherwise, open the view dialog for text-based documents
        setSelectedDocument(document);
        setViewDialogOpen(true);
        setLoadingContent(true);

        try {
            const response = await documentReviewAPI.getDocumentContent(document.documentTemplateId);
            setDocumentContent(response.data.content || '');
        } catch (err: any) {
            console.error('Error loading document content:', err);
            setError(err.response?.data?.message || 'Failed to load document content');
        } finally {
            setLoadingContent(false);
        }
    };

    const handleDownloadDocument = async (documentTemplateId: string, title: string) => {
        try {
            const response = await documentReviewAPI.downloadDocument(documentTemplateId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error('Error downloading document:', err);
            setError(err.response?.data?.message || 'Failed to download document');
        }
    };

    const handleCloseDialog = () => {
        setViewDialogOpen(false);
        setSelectedDocument(null);
        setDocumentContent('');
    };

    const getDocumentIcon = (type: string) => {
        switch (type) {
            case 'pdf':
                return <PdfIcon />;
            case 'html':
            case 'markdown':
            case 'text':
                return <ArticleIcon />;
            default:
                return <DescriptionIcon />;
        }
    };

    const renderDocumentContent = () => {
        if (!selectedDocument) return null;

        if (loadingContent) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            );
        }

        if (selectedDocument.documentTemplate.type === 'pdf') {
            return (
                <Box textAlign="center" py={4}>
                    <PdfIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="body1" gutterBottom>
                        This is a PDF document. Click the download button below to view or save it.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDocument(selectedDocument.documentTemplateId, selectedDocument.documentTemplate.title)}
                        sx={{ mt: 2 }}
                    >
                        Download PDF
                    </Button>
                </Box>
            );
        }

        if (selectedDocument.documentTemplate.type === 'html') {
            return (
                <Box 
                    dangerouslySetInnerHTML={{ __html: documentContent }}
                    sx={{ 
                        '& img': { maxWidth: '100%', height: 'auto' },
                        '& table': { borderCollapse: 'collapse', width: '100%' },
                        '& td, & th': { border: '1px solid #ddd', padding: '8px' }
                    }}
                />
            );
        }

        if (selectedDocument.documentTemplate.type === 'markdown') {
            // Simple markdown rendering - could be enhanced with a markdown library
            return (
                <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {documentContent}
                </Box>
            );
        }

        return (
            <Box sx={{ whiteSpace: 'pre-wrap' }}>
                {documentContent}
            </Box>
        );
    };

    if (!isReady) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <DescriptionIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" component="h1">
                            Documents
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Documents that were reviewed and apply to your membership
                        </Typography>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : acceptedDocuments.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Documents Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            You haven't accepted any documents yet.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Date Accepted</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {acceptedDocuments.map((acceptance) => (
                                    <TableRow key={acceptance.id} hover>
                                        <TableCell>
                                            <Box 
                                                display="flex" 
                                                alignItems="center"
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        '& .document-title': {
                                                            textDecoration: 'underline',
                                                            color: 'primary.main'
                                                        }
                                                    }
                                                }}
                                                onClick={() => handleDocumentClick(acceptance)}
                                            >
                                                <Box sx={{ mr: 1, display: 'flex', color: 'primary.main' }}>
                                                    {getDocumentIcon(acceptance.documentTemplate.type)}
                                                </Box>
                                                <Box>
                                                    <Typography 
                                                        variant="body1" 
                                                        fontWeight="medium"
                                                        className="document-title"
                                                    >
                                                        {acceptance.documentTemplate.title}
                                                    </Typography>
                                                    {acceptance.documentTemplate.description && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {acceptance.documentTemplate.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={acceptance.documentTemplate.type.toUpperCase()} 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(acceptance.acceptedAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label="Accepted"
                                                color="success"
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* View Document Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        {selectedDocument && getDocumentIcon(selectedDocument.documentTemplate.type)}
                        <Box ml={1}>
                            {selectedDocument?.documentTemplate.title}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedDocument && (
                        <>
                            <Box mb={2}>
                                <Typography variant="caption" color="text.secondary">
                                    Accepted on: {formatDate(selectedDocument.acceptedAt)}
                                </Typography>
                            </Box>
                            {renderDocumentContent()}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedDocument?.documentTemplate.type === 'pdf' && (
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                if (selectedDocument) {
                                    handleDownloadDocument(
                                        selectedDocument.documentTemplateId,
                                        selectedDocument.documentTemplate.title
                                    );
                                }
                            }}
                        >
                            Download
                        </Button>
                    )}
                    <Button onClick={handleCloseDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default DocumentReviewPage;
